/**
 * useAdminApi.ts — Admin-scoped API hooks wrapping billing-sdk BillingClient
 *
 * Purpose:
 *   Provides TanStack Query hooks for all admin data operations. Each hook
 *   injects the operator's JWT from AdminAuthContext as an Authorization
 *   Bearer header by passing the token as the `apiKey` to BillingClient.
 *
 * Public API:
 *   - useAdminCustomers(params?)  Paginated customer list
 *   - useAdminPlans()             Plan list via direct REST fetch
 *   - useAdminSubscriptions(params?)  Paginated subscription list
 *   - useAdminInvoices(params?)   Paginated invoice list
 *
 * Architecture Context:
 *   All admin hooks share one BillingClient instance per token via useMemo.
 *   The client is re-created when the JWT changes (login/logout).
 *   BillingClient(options) accepts { baseUrl, apiKey } per billing-sdk contract.
 *
 *   ATP 20.07 dependency: BillingClient from billing-sdk is available.
 *   ATP 20.06 dependency: /admin/* REST routes — getPlans not yet in SDK.
 *
 * @module packages/billing-admin/src/hooks/useAdminApi
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BillingClient } from '@rule42/billing-sdk';
import type { PaginatedList, ListParams } from '@rule42/billing-sdk';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import type { Customer, Subscription, Invoice } from '@rule42/billing-types';

// ---------------------------------------------------------------------------
// Plan type (billing-sdk does not export a Plan type yet — local fallback)
// ---------------------------------------------------------------------------

/** Minimal plan shape used by the admin console. Extends once SDK is updated. */
interface PlanSummary {
  id: string;
  name: string;
  priceMonthly: number;
  currency: string;
  active: boolean;
}

export interface AdminCustomerSummary {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  description?: string | null;
  stripe_customer_id?: string | null;
  created_at: string;
}

interface AdminCustomerListResponse {
  customers: AdminCustomerSummary[];
  nextCursor: string | null;
}

export interface AdminCustomerMutationInput {
  name?: string;
  email?: string;
  phone?: string;
  description?: string;
  address?: {
    line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string;
  };
  shipping?: {
    name?: string; phone?: string;
    address?: { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string };
  };
  currency?: string;
  invoice_prefix?: string;
  preferred_locales?: string[];
  invoice_settings?: {
    default_payment_method?: string;
    custom_fields?: Array<{ name: string; value: string }>;
    footer?: string;
  };
  metadata?: Record<string, string>;
}

type AdminCustomerListParams = ListParams & {
  search?: string;
};

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

/**
 * useAdminClient — creates an authenticated BillingClient for admin operations.
 *
 * Uses the JWT from AdminAuthContext as the `apiKey` so every SDK request
 * carries an `Authorization: Bearer <token>` header.
 *
 * @returns BillingClient instance or null when unauthenticated
 * @since 2026-05-31 (AT-50.01)
 */
function useAdminClient(): BillingClient | null {
  const { token, apiBaseUrl } = useAdminAuth();
  return useMemo(() => {
    if (!token) return null;
    // ARCHITECTURE: BillingClientOptions = { baseUrl, apiKey }
    //   apiKey carries the admin JWT — SDK appends Authorization header.
    return new BillingClient({ baseUrl: apiBaseUrl, apiKey: token });
  }, [token, apiBaseUrl]);
}

// ---------------------------------------------------------------------------
// Customer hook
// ---------------------------------------------------------------------------

/**
 * useAdminCustomers — returns paginated customer list.
 *
 * @param params - Optional list/pagination params (ListParams)
 * @since 2026-05-31 (AT-50.01)
 */
export function useAdminCustomers(params?: AdminCustomerListParams) {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<AdminCustomerListResponse, Error>({
    queryKey: ['admin', 'customers', params],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const query = new URLSearchParams();
      if (typeof params?.search === 'string' && params.search.trim()) {
        query.set('search', params.search.trim());
      }

      const suffix = query.toString() ? `?${query.toString()}` : '';
      const res = await fetch(`${apiBaseUrl}/admin/customers${suffix}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch customers: ${res.status}`);
      }

      return res.json() as Promise<AdminCustomerListResponse>;
    },
    enabled: !!token,
  });
}

// ---------------------------------------------------------------------------
// Plans hook
// ---------------------------------------------------------------------------

/**
 * useAdminPlans — returns the list of billing plans via direct REST fetch.
 *
 * NOTE: BillingClient does not yet have a plans.list() method (ATP 20.06/20.07
 * dependency). This hook calls the admin REST endpoint directly until the SDK
 * method is available.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export function useAdminPlans() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<PlanSummary[], Error>({
    queryKey: ['admin', 'plans'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch plans: ${res.status}`);
      return res.json() as Promise<PlanSummary[]>;
    },
    enabled: !!token,
  });
}

// ---------------------------------------------------------------------------
// Subscriptions hook
// ---------------------------------------------------------------------------

/**
 * useAdminSubscriptions — returns paginated subscription list.
 *
 * @param params - Optional list/pagination params (ListParams)
 * @since 2026-05-31 (AT-50.01)
 */
export function useAdminSubscriptions(params?: ListParams & { customerId?: string }) {
  const client = useAdminClient();
  return useQuery<PaginatedList<Subscription>, Error>({
    queryKey: ['admin', 'subscriptions', params],
    queryFn: async () => {
      if (!client) throw new Error('Not authenticated');
      return client.subscriptions.list(params);
    },
    enabled: !!client,
  });
}

// ---------------------------------------------------------------------------
// Invoices hook
// ---------------------------------------------------------------------------

/**
 * useAdminInvoices — returns invoice list from admin-scoped endpoint.
 *
 * @param params - Optional filter params
 * @since 2026-06-04
 */
export function useAdminInvoices(params?: { limit?: number; status?: string }) {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<PaginatedList<Invoice>, Error>({
    queryKey: ['admin', 'invoices', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.status) qs.set('status', params.status);
      const res = await fetch(`${apiBaseUrl}/admin/invoices?${qs.toString()}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Billing API error ${res.status}: ${res.statusText}`);
      const json = await res.json() as { data: Invoice[]; meta: { total: number } };
      return { data: json.data, total: json.meta.total };
    },
    enabled: !!token,
  });
}

export function useCreateAdminCustomer() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation<AdminCustomerSummary, Error, AdminCustomerMutationInput>({
    mutationFn: async (input) => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/customers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!res.ok) {
        throw new Error(`Failed to create customer: ${res.status}`);
      }

      const body = (await res.json()) as { data: AdminCustomerSummary };
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useUpdateAdminCustomer() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation<AdminCustomerSummary, Error, { customerId: string; input: AdminCustomerMutationInput }>({
    mutationFn: async ({ customerId, input }) => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!res.ok) {
        throw new Error(`Failed to update customer: ${res.status}`);
      }

      const body = (await res.json()) as { data: AdminCustomerSummary };
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useDeleteAdminCustomer() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (customerId) => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/customers/${customerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to delete customer: ${res.status}`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}
